import React, { useEffect, useRef, useState } from 'react';
import { UserSettings } from '../../types';
import { checkIsHighTierPremium } from '../../utils/userUtils';
import { apiService } from '../../services/apiService';
import { API_EDU_PREFIX, normalizeAdSenseClientId } from '../../config';
import { canShowAdSlot } from '../../utils/adPolicy';

// --- Cơ chế Batching Requests ---
let adEventQueue: any[] = [];
let adEventTimer: any = null;

const flushAdEvents = () => {
    if (adEventQueue.length === 0) return;
    
    const eventsToSend = [...adEventQueue];
    adEventQueue = [];
    
    const url = `${API_EDU_PREFIX}/ads/record`;
    
    apiService.post(url, eventsToSend).catch(() => {});
};

const queueAdEvent = (event: any) => {
    adEventQueue.push(event);
    if (adEventTimer) clearTimeout(adEventTimer);
    adEventTimer = setTimeout(flushAdEvents, 800); // Chờ 800ms để gom các quảng cáo khác trên cùng trang
};
// --------------------------------

type Size = [number, number];

export const AdSlot: React.FC<{ slot: any; slotId?: string; className?: string; user?: UserSettings | null }>
  = ({ slot, slotId = '', className, user }) => {
  const impressionRecorded = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isBannerSlot = (slotId || '').includes('banner') || slotId === 'loading_banner';
  const [adContainerReady, setAdContainerReady] = useState(isBannerSlot);

  // RULE: Don't show ads for high-tier premium users if they have disabled them
  const isHighTier = checkIsHighTierPremium(user);
  const showAds = !isHighTier || user?.adsEnabled !== false;
  const routeAllowsAds = typeof window === 'undefined'
    ? false
    : canShowAdSlot(window.location.pathname, slotId, user);

  // Chỉ render AdSense khi container có width > 0 (tránh lỗi "No slot size for availableWidth=0")
  useEffect(() => {
    if (!slot || slot.type !== 'adsense' || slot.enabled === false) return;
    const el = containerRef.current;
    if (!el) return;

    const checkReady = () => {
      const rect = el.getBoundingClientRect();
      const style = window.getComputedStyle(el);
      const isHidden = style.display === 'none' || style.visibility === 'hidden';
      const hasWidth = rect.width > 0 && !isHidden;
      if (hasWidth) setAdContainerReady((prev) => prev || true);
    };

    checkReady();
    const ro = new ResizeObserver(checkReady);
    ro.observe(el);
    const t1 = setTimeout(checkReady, 200);
    const t2 = setTimeout(checkReady, 600);
    const t3 = setTimeout(checkReady, 1200);
    return () => {
      ro.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [slot]);

  useEffect(() => {
    if (!showAds || !routeAllowsAds || !slot || slot.enabled === false) return;
    if (!impressionRecorded.current) {
        if (typeof (window as any).gtag === 'function') {
            (window as any).gtag('event', 'ad_impression', {
                'event_category': 'Advertisement',
                'ad_slot_id': slotId,
                'ad_type': slot.type,
                'non_interaction': true 
            });
        }
        const internalTrackingEnabled = (window as any).__EDUAPP_ADS__?.internal_tracking_enabled !== false;
        const timer = setTimeout(() => {
            if (internalTrackingEnabled) {
                queueAdEvent({ slot_id: slotId, ad_type: slot.type, event_type: 'impression', page_url: window.location.href });
            }
            impressionRecorded.current = true;
        }, 1000);
        return () => clearTimeout(timer);
    }
  }, [showAds, routeAllowsAds, slot, slotId]);

  // Push AdSense chỉ khi container đã có width > 0 (tránh "No slot size for availableWidth=0")
  useEffect(() => {
    if (showAds && routeAllowsAds && slot?.type === 'adsense' && slot.enabled !== false && adContainerReady) {
        const timer = setTimeout(() => {
            try {
                (window as any).adsbygoogle = (window as any).adsbygoogle || [];
                (window as any).adsbygoogle.push({});
            } catch (e) {
                console.error("AdSense push error:", e);
            }
        }, 300);
        return () => clearTimeout(timer);
    }
  }, [showAds, routeAllowsAds, slot, adContainerReady]);

  const handleAdClick = () => {
    // 1. Gửi sự kiện Click sang Google Analytics (GA4)
    if (typeof (window as any).gtag === 'function') {
        (window as any).gtag('event', 'ad_click', {
            'event_category': 'Advertisement',
            'ad_slot_id': slotId,
            'ad_type': slot.type
        });
    }

    // 2. Ghi nhận nội bộ (nếu bật)
    const internalTrackingEnabled = (window as any).__EDUAPP_ADS__?.internal_tracking_enabled !== false;
    if (internalTrackingEnabled) {
        const url = `${API_EDU_PREFIX}/ads/record`;
        
        // Lượt click quan trọng và đơn lẻ nên ta gửi ngay lập tức không gom nhóm
        apiService.post(url, {
            slot_id: slotId,
            ad_type: slot.type,
            event_type: 'click',
            page_url: window.location.href
        }).catch(() => {});
    }
  };

  if (!showAds || !routeAllowsAds) {
    return null;
  }

  if (!slot || slot.enabled === false) return null;
  const sizes: Size[] = Array.isArray(slot.sizes) ? slot.sizes : [];
  const [w, h] = sizes[0] || [728, 90];
  const style: React.CSSProperties = { 
    width: w > 0 ? w : '100%', 
    height: h > 0 ? h : 'auto',
    minHeight: h > 0 ? h : '90px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: '#f9fafb', // light gray background
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    padding: '8px'
  };

  const adContent = () => {
    let client = normalizeAdSenseClientId(slot.client);
    let adSlotId = slot.slot;
    let placementId = slot.placement_id;

    if ((window as any).__EDUAPP_ADS__?.test_mode) {
        if (slot.type === 'adsense') {
            client = 'ca-pub-3940256099942544'; // Google Test AdSense Client
            adSlotId = '6312122020'; // Google Test AdSense Slot
        } else if (slot.type === 'facebook') {
            placementId = 'YOUR_PLACEMENT_ID'; // FB Test Placement (handled by FB SDK)
        }
    }

    if (slot.type === 'adsense' && client && adSlotId) {
      if (!adContainerReady) {
        return <div style={{ minHeight: h > 0 ? h : 90, width: '100%' }} aria-hidden="true" />;
      }
      const isTestMode = (window as any).__EDUAPP_ADS__?.test_mode;
      const isHorizontal = (slotId || '').includes('banner') || slotId === 'loading_banner';
      return (
        <ins className={`adsbygoogle ${className || ''}`}
             style={{ display: 'block', width: '100%', ...(isHorizontal && { minWidth: 320 }), height: h > 0 ? h : 'auto' }}
             data-ad-client={client}
             data-ad-slot={adSlotId}
             data-ad-format={isHorizontal ? 'horizontal' : 'auto'}
             data-ad-test={isTestMode ? 'on' : undefined}
             data-full-width-responsive="true"></ins>
      );
    }

    if (slot.type === 'facebook' && placementId) {
        // Facebook Audience Network example (integration varies by SDK)
        return (
            <div 
                className="fb-ad-placeholder" 
                data-placementid={slot.placement_id} 
                style={{ width: '100%', height: h > 0 ? h : 'auto' }}
            >
                {/* Facebook SDK would typically render here */}
                <p className="text-[10px] text-gray-400">Facebook Ad Placeholder</p>
            </div>
        );
    }

    if (slot.type === 'html' && slot.html) {
      return <div className={className} style={{ width: '100%' }} onClick={handleAdClick} dangerouslySetInnerHTML={{ __html: slot.html }} />;
    }
    if (slot.type === 'iframe' && slot.iframe_src) {
      return <iframe title="Advertisement" className={className} src={slot.iframe_src} style={{ width: '100%', height: h > 0 ? h : 'auto' }} frameBorder={0} />;
    }
    if (slot.type === 'image' && (slot.image_url || (window as any).__EDUAPP_ADS__?.test_mode)) {
      const imgUrl = slot.image_url || 'https://placehold.co/728x90?text=Horizontal+Banner+Ad';
      return (
        <a className={className} href={slot.click_url || '#'} onClick={handleAdClick} target="_blank" rel="noreferrer" style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center' }}>
          <img src={imgUrl} alt="Advertisement" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover', borderRadius: '12px' }} />
        </a>
      );
    }
    return (
        <div className="text-center p-4 border-2 border-dashed border-gray-200 rounded-xl w-full">
            <p className="text-xs text-gray-400 italic">No advertisement content</p>
            {(window as any).__EDUAPP_ADS__?.test_mode && (
                <p className="text-[10px] text-indigo-500 font-bold mt-1">[AD TEST MODE ACTIVE]</p>
            )}
        </div>
    );
  };

  return (
    <div className="flex flex-col items-center w-full max-w-5xl mx-auto py-6">
        <div className="flex items-center space-x-2 mb-3 w-full justify-start pl-1 opacity-60">
            <span className="text-[9px] uppercase tracking-[0.2em] font-black text-slate-400">
                {(window as any).__EDUAPP_ADS__?.test_mode ? 'QUẢNG CÁO THỬ NGHIỆM' : 'TÀI TRỢ'}
            </span>
            <div className={`h-[1px] flex-grow ${ (window as any).__EDUAPP_ADS__?.test_mode ? 'bg-indigo-100' : 'bg-slate-100' }`}></div>
        </div>
        <div ref={containerRef} className={`ad-slot-container ${className || ''} hover:shadow-md transition-shadow duration-300`} style={style}>
            {adContent()}
        </div>
        {(window as any).__EDUAPP_ADS__?.test_mode && (
            <p className="mt-2 text-[9px] text-indigo-400 font-bold italic">
                Mẹo: Bạn có thể cấu hình kích thước hiển thị ngang trong Admin để tối ưu trải nghiệm.
            </p>
        )}
        {isHighTier && !(window as any).__EDUAPP_ADS__?.test_mode && (
            <p className="mt-2 text-[10px] text-gray-400">
                Bạn là người dùng Cao cấp. Bạn có thể tắt quảng cáo trong phần Cài đặt.
            </p>
        )}
    </div>
  );
};
